import { useRef, useState } from 'react';
import type { FeedRole } from '@frisk/shared';
import { api } from '../api';
import { t } from '../theme';
import { WasteIcon } from './widgets/WasteIcon';
import { useStore } from '../store';

/**
 * Import a downloaded .ics.
 *
 * Many Kommunen publish an Abfuhrkalender as a file with no subscribable link,
 * so the file is all you get. That makes this a snapshot rather than a
 * subscription — usually one per year — and the UI says so instead of implying
 * it will keep itself current.
 *
 * The file is read in the browser and posted as text: an .ics is text anyway,
 * and it saves a multipart dependency on the server.
 */
export function ImportFeed({
  initialRole = 'waste',
  onClose,
  onDone,
}: {
  /** Defaults to waste: a downloaded file is nearly always an Abfuhrkalender. */
  initialRole?: FeedRole;
  onClose: () => void;
  onDone: () => void;
}) {
  const flash = useStore((s) => s.flash);
  const [role, setRole] = useState<FeedRole>(initialRole);
  const [label, setLabel] = useState('Abfuhrkalender');
  /** Cleared once the name is the user's own, so switching kind stops guessing. */
  const [labelTouched, setLabelTouched] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (!file || !label.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const ics = await file.text();
      const created = await api.importFeed({
        label: label.trim(),
        role,
        filename: file.name,
        ics,
      });
      onDone();
      onClose();
      flash(
        created.sync.error
          ? `${label}: ${created.sync.error}`
          : `${label}: ${created.sync.count} Termine importiert`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'oklch(0.12 0.01 60 / 0.72)',
        backdropFilter: 'blur(8px)',
        animation: 'fade 160ms ease-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 35,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{
          width: 620,
          maxWidth: '92vw',
          borderRadius: 30,
          background: t.surface,
          border: '1px solid oklch(1 0 0 / 0.09)',
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          animation: 'sheet 200ms ease-out',
        }}
      >
        <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>
          ICS-DATEI IMPORTIEREN
        </div>
        <div style={{ fontSize: 30, fontWeight: 500, letterSpacing: '-0.035em' }}>Was für ein Kalender?</div>

        {/* Asked rather than guessed. Reading the file and auto-tagging would
            be right often and quietly wrong the rest of the time, and a
            calendar that silently decides it is about rubbish is worse than
            one question. */}
        <div style={{ display: 'flex', gap: 10 }}>
          {([
            { value: 'waste' as const, label: 'Müllkalender', icon: true },
            { value: 'family' as const, label: 'Normaler Kalender', icon: false },
          ]).map((option) => {
            const on = role === option.value;
            return (
              <div
                key={option.value}
                className="press"
                onClick={() => {
                  setRole(option.value);
                  // Only overwrite a name the user has not typed themselves.
                  if (!labelTouched) setLabel(option.value === 'waste' ? 'Abfuhrkalender' : '');
                }}
                style={{
                  flex: 1,
                  height: 62,
                  borderRadius: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 9,
                  fontSize: 18,
                  fontWeight: 500,
                  background: on ? 'oklch(0.3 0.012 60)' : t.surfaceSunken,
                  border: on ? `1px solid ${t.accent}` : t.hairline,
                  color: on ? t.ink : 'oklch(0.62 0.015 70)',
                }}
              >
                {option.icon && <WasteIcon size={20} />}
                {option.label}
              </div>
            );
          })}
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>NAME</span>
          <input
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              setLabelTouched(true);
            }}
            placeholder="z. B. Abfuhrkalender 2026"
            autoComplete="off"
            style={{
              background: 'oklch(0.2 0.012 60)',
              border: '1px solid oklch(1 0 0 / 0.12)',
              borderRadius: 16,
              padding: '16px 18px',
              color: t.ink,
              caretColor: t.accent,
              fontSize: 22,
              fontFamily: t.sans,
              outline: 'none',
            }}
          />
        </label>

        <input
          ref={picker}
          type="file"
          accept=".ics,text/calendar"
          onChange={(e) => {
            const chosen = e.target.files?.[0] ?? null;
            setFile(chosen);
            setError(null);
            if (chosen && !label.trim()) setLabel(chosen.name.replace(/\.ics$/i, ''));
          }}
          style={{ display: 'none' }}
        />
        <div
          className="press"
          onClick={() => picker.current?.click()}
          style={{
            height: 76,
            borderRadius: 18,
            border: file ? `1px solid ${t.accent}` : '1px dashed oklch(1 0 0 / 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            fontSize: 19,
            fontWeight: 500,
            color: file ? t.ink : 'oklch(0.75 0.015 70)',
            padding: '0 20px',
            textAlign: 'center',
          }}
        >
          {file ? `${file.name} · ${Math.max(1, Math.round(file.size / 1024))} KB` : 'Datei auswählen …'}
        </div>

        {error && <div style={{ fontSize: 16, color: 'oklch(0.82 0.14 30)', textWrap: 'pretty' }}>{error}</div>}

        <div style={{ fontSize: 15, color: t.inkFaint, textWrap: 'pretty' }}>
          Eine Datei ist eine Momentaufnahme, kein Abo — sie aktualisiert sich nicht von selbst.
          Für das nächste Jahr einfach die neue Datei hochladen. Wenn deine Kommune einen Link
          anbietet, ist der die bessere Wahl.
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div
            className="press"
            onClick={onClose}
            style={{
              flex: 1,
              height: 64,
              borderRadius: 18,
              border: t.hairlineStrong,
              color: t.inkMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 19,
              fontWeight: 500,
            }}
          >
            Abbrechen
          </div>
          <div
            className="press"
            onClick={() => void submit()}
            style={{
              flex: 1.4,
              height: 64,
              borderRadius: 18,
              background: file && label.trim() ? t.accent : t.control,
              color: file && label.trim() ? t.accentInk : t.inkFaint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 600,
            }}
          >
            {busy ? 'Wird importiert …' : 'Importieren'}
          </div>
        </div>
      </form>
    </div>
  );
}
